import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TextField.scss';

const AppTextField: FC<AppTextFieldProps> = ({
  id,
  label,
  disabled,
  required,
  placeholder,
  value,
  defaultValue,
  type,
  isRow = false,
  autoFocus,
  maxLength,
  onChange,
  error,
  icon,
  enableTrim,
  variant
}) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const isTypePassword = useMemo(() => type === 'password', [type]);

  const inputType = useMemo(() => {
    if (!isTypePassword) return type;
    return showPassword ? 'text' : 'password';
  }, [type, showPassword, isTypePassword]);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('message.MSE00001', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error, label, t]);

  const handleShowPassword = () => setShowPassword((show) => !show);

  const handleMouseIcon = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleTrimOnBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange?: React.ChangeEventHandler<HTMLInputElement>
  ) => {
    if (!enableTrim) return;
    const trimmedValue = event.target.value.trim();
    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: trimmedValue,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
  
    if (onChange) {
      onChange(newEvent);
    }
  };

  return (
    <Grid
      container
      className="TextField"
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      <Grid item xs={8}>
        <TextField
          className="hide-password-reveal"
          autoComplete='off'
          fullWidth
          id={id}
          name={id}
          disabled={disabled}
          type={inputType}
          label={t(label!)}
          required={required}
          placeholder={t(placeholder!)}
          value={value}
          defaultValue={defaultValue}
          autoFocus={autoFocus}
          variant={variant}
          slotProps={{
            htmlInput: {
              maxLength: isTypePassword ? 32 : maxLength,
            },
            inputLabel: {
              shrink: true,
            },
            input: {
              endAdornment: !isTypePassword ? (icon) ? (
                <InputAdornment position="end">
                  <IconButton
                    tabIndex={-1}
                    aria-label="icon-input"
                    edge="end"
                  >
                    {icon}
                  </IconButton>
                </InputAdornment>
              ) : undefined : (
                <InputAdornment position="end">
                  <IconButton
                    tabIndex={-1}
                    aria-label="toggle password visibility"
                    onClick={handleShowPassword}
                    onMouseDown={handleMouseIcon}
                    onMouseUp={handleMouseIcon}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          onChange={onChange}
          onBlur={(event) => handleTrimOnBlur(event, onChange)}
          error={!!error?.type}
          helperText={errorText}
        />
      </Grid>
    </Grid>
  );
};

export default AppTextField;
